import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { CellContext } from "@/lib/Components/DataTable/types";
import { SelectFilter } from "@/lib/Components/DataTable/Filters/SelectFilter";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import {
  AgentRunListItem,
  agentRunListQuery,
} from "@/app/Agents/GraphQL/agentRunQueries";
import { AgentRunDialog } from "@/app/Agents/Components/Dialogs/AgentRunDialog";
import {
  AGENT_RUN_KIND_LABEL,
  AGENT_RUN_STATE_TONE,
} from "@/app/Agents/Utils/agentRunMaps";
import { Badge } from "@/components/catalyst/badge";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
} from "@/components/catalyst/navbar";
import { AgentRunKind, AgentRunState } from "@/gql/graphql";

const STATE_TABS: { label: string; state?: AgentRunState }[] = [
  { label: "All" },
  { label: "Needs attention", state: AgentRunState.NeedsAttention },
  { label: "Failed", state: AgentRunState.Failed },
  { label: "Done", state: AgentRunState.Done },
  { label: "Not relevant", state: AgentRunState.NotRelevant },
];

const KIND_OPTIONS = [
  {
    label: AGENT_RUN_KIND_LABEL[AgentRunKind.MailboxEmail],
    value: AgentRunKind.MailboxEmail,
  },
  {
    label: AGENT_RUN_KIND_LABEL[AgentRunKind.ThlListImport],
    value: AgentRunKind.ThlListImport,
  },
];

export function AgentRunTable({
  state,
  kind,
  onStateChange,
  onKindChange,
}: {
  state?: AgentRunState;
  kind?: AgentRunKind;
  onStateChange: (state?: AgentRunState) => void;
  onKindChange: (kind?: AgentRunKind) => void;
}) {
  const columns: DataTableColDef<AgentRunListItem, any, any>[] = [
    {
      id: "title",
      header: "Run",
      accessorFn: (row) => row,
      cell: AgentRunTitleCell,
    },
    {
      id: "kind",
      header: "Agent",
      accessorFn: (row) => row.kind,
      cell: ({ getValue }) => (
        <span className="text-xs text-gray-600">
          {AGENT_RUN_KIND_LABEL[getValue() as AgentRunKind]}
        </span>
      ),
    },
    {
      id: "subtitle",
      header: "Source",
      accessorFn: (row) => row.subtitle,
      cell: ({ getValue }) => (
        <span className="text-xs text-gray-600">{getValue() ?? "--"}</span>
      ),
    },
    {
      id: "state",
      header: "Status",
      accessorFn: (row) => row,
      cell: ({ getValue }) => {
        const run: AgentRunListItem = getValue();

        return (
          <Badge color={AGENT_RUN_STATE_TONE[run.state]}>
            {run.status.replaceAll("_", " ").toLowerCase()}
          </Badge>
        );
      },
    },
    {
      id: "note",
      header: "Outcome",
      accessorFn: (row) => row.note,
      cell: ({ getValue }) => (
        <span className="line-clamp-2 text-xs text-gray-500">
          {getValue() ?? "--"}
        </span>
      ),
    },
    {
      id: "created",
      header: "When",
      accessorFn: (row) => row.created_at,
      cell: TimeSinceNowCell,
    },
  ];

  return (
    <div>
      <StateTabs state={state} onStateChange={onStateChange} />
      <DataTable
        columns={columns}
        document={agentRunListQuery}
        searchable={false}
        filters={(table) => (
          <SelectFilter
            label="Agent"
            options={KIND_OPTIONS}
            selected={kind ?? null}
            onChange={(value) => {
              onKindChange(value ?? undefined);
              table.setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
        )}
        getQueryVariables={({ pagination }) => ({
          first: pagination.pageSize,
          page: pagination.pageIndex,
          kind: kind ? [kind] : undefined,
          state: state ? [state] : undefined,
        })}
        accessor={(data) => data.agentRuns}
        id="agent-runs"
      />
    </div>
  );
}

function StateTabs({
  state,
  onStateChange,
}: {
  state?: AgentRunState;
  onStateChange: (state?: AgentRunState) => void;
}) {
  return (
    <div className="relative border-gray-200 sm:border-b">
      <Navbar className="-mb-px">
        <NavbarSection id="agent-runs-navbar">
          {STATE_TABS.map((tab) => (
            <NavbarItem
              key={tab.label}
              current={state === tab.state}
              onClick={() => onStateChange(tab.state)}
            >
              {tab.label}
            </NavbarItem>
          ))}
        </NavbarSection>
      </Navbar>
    </div>
  );
}

function AgentRunTitleCell({ row }: CellContext<AgentRunListItem, any>) {
  const run = row.original;
  const { open } = useDialog(AgentRunDialog);

  return (
    <button
      onClick={() => open({ kind: run.kind, id: run.id })}
      className="text-left font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
    >
      {run.title}
    </button>
  );
}
